import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown, Camera, Check, CloudUpload, MousePointer2, RotateCcw } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { useTranslation } from '../presentation/common/useTranslation.js';
import { usePresentation } from '../presentation/PresentationProvider';
import GoodvaleLogo from './GoodvaleLogo';
import ExperienceReward from './ExperienceReward';
import { useTheme } from '../hooks/useTheme';
import LandingCourtyardArtwork from './LandingCourtyardArtwork';
import paperArt from '../presentation/common/scenes/paper.webp';
import { CLEANUP_BOUNDS } from './landingCleanupGeometry';
import { landingCourtyardLayout } from './landingCourtyardLayout';
import './LandingCleanupScene.css';

const HOME = { x: 193, y: 365 };
const REWARD = 25;
export default function LandingCleanupScene({ loading = false, children }) {
  const { t, i18n } = useTranslation();
  const { mode } = usePresentation();
  const { theme } = useTheme();
  const id = useId().replaceAll(':', '');
  const reducedMotion = useReducedMotion();
  const stageRef = useRef(null);
  const sceneRef = useRef(null);
  const storyRef = useRef(null);
  const paperRef = useRef(null);
  const dragRef = useRef(null);
  const focusStoryRef = useRef(false);
  const focusPaperRef = useRef(false);
  const [bounds, setBounds] = useState(CLEANUP_BOUNDS);
  const [snapshot, setSnapshot] = useState(null);
  const [phase, setPhase] = useState('falling');
  const [position, setPosition] = useState(HOME);
  const [nearBin, setNearBin] = useState(false);
  const [xp, setXp] = useState(0);
  const [round, setRound] = useState(0);
  const { mouth, drop } = landingCourtyardLayout(bounds, mode);

  useLayoutEffect(() => {
    const updateBounds = () => {
      const scene = sceneRef.current.getBoundingClientRect();
      const stage = stageRef.current.getBoundingClientRect();
      const scale = stage.width / 600;
      if (!scale) return;
      const clearance = parseFloat(getComputedStyle(sceneRef.current).getPropertyValue('--cleanup-ground-clearance')) || 168;
      const next = { left: (scene.left - stage.left) / scale, top: (scene.top - stage.top) / scale, right: (scene.right - stage.left) / scale, bottom: (scene.bottom - stage.top) / scale, pixelsPerUnit: scale, safeBottom: (scene.bottom - clearance - stage.top) / scale };
      setBounds(previous => Object.keys(next).every(key => Math.abs(previous[key] - next[key]) < .1) ? previous : next);
    };
    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    observer.observe(sceneRef.current);
    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Freeze the world, theme and crop at shutter time, even when settings change during upload.
    if (phase !== 'photo' || snapshot) return;
    const scene = sceneRef.current.getBoundingClientRect();
    setSnapshot({ mode, theme, bounds: { ...bounds }, aspect: scene.width / scene.height });
  }, [phase, snapshot, bounds, mode, theme]);

  useEffect(() => {
    if (loading) return;
    const next = {
      falling: ['ready', reducedMotion ? 0 : 1400],
      returning: ['ready', reducedMotion ? 0 : 400],
      tossing: ['photo', reducedMotion ? 100 : 650],
      photo: ['upload', 1500],
      upload: ['reward', 1900],
      complete: ['intro', 1450],
    }[phase];
    if (!next) return;
    const timer = window.setTimeout(() => setPhase(next[0]), next[1]);
    return () => window.clearTimeout(timer);
  }, [phase, loading, reducedMotion, round]);

  useEffect(() => {
    if (phase === 'intro' && focusStoryRef.current) {
      focusStoryRef.current = false;
      storyRef.current?.querySelector('h1')?.focus({ preventScroll: true });
    }
    if (phase === 'ready' && focusPaperRef.current) {
      focusPaperRef.current = false;
      paperRef.current?.focus({ preventScroll: true });
    }
  }, [phase]);

  function scenePoint(event) {
    const rect = stageRef.current.getBoundingClientRect();
    return { x: (event.clientX - rect.left) / rect.width * 600, y: (event.clientY - rect.top) / rect.height * 460 };
  }

  function isOverBin(point) {
    return point.x >= drop.left && point.x <= drop.right && point.y >= drop.top && point.y <= drop.bottom;
  }

  function toss() {
    dragRef.current = null;
    setNearBin(false);
    setPosition({ x: mouth.x, y: mouth.y });
    setPhase('tossing');
  }

  function startDrag(event) {
    if (phase !== 'ready' || !event.isPrimary || event.button !== 0) return;
    const point = scenePoint(event);
    dragRef.current = { pointerId: event.pointerId, x: position.x - point.x, y: position.y - point.y };
    event.currentTarget.setPointerCapture(event.pointerId);
    setPhase('dragging');
  }

  function dragPoint(event) {
    const point = scenePoint(event);
    return {
      x: Math.max(bounds.left + 36, Math.min(bounds.right - 36, point.x + dragRef.current.x)),
      y: Math.max(bounds.top + 35, Math.min(bounds.bottom - 35, point.y + dragRef.current.y)),
    };
  }

  function moveDrag(event) {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    const point = dragPoint(event);
    setPosition(point);
    setNearBin(isOverBin(point));
  }

  function cancelDrag() {
    if (!dragRef.current) return;
    dragRef.current = null;
    setPosition(HOME);
    setNearBin(false);
    setPhase('returning');
  }

  function endDrag(event) {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    const point = dragPoint(event);
    if (isOverBin(point)) toss();
    else cancelDrag();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function replay() {
    const dragging = dragRef.current;
    dragRef.current = null;
    if (dragging && paperRef.current?.hasPointerCapture(dragging.pointerId)) paperRef.current.releasePointerCapture(dragging.pointerId);
    focusStoryRef.current = false;
    focusPaperRef.current = true;
    setPosition(HOME);
    setXp(0);
    setSnapshot(null);
    setNearBin(false);
    setRound(value => value + 1);
    setPhase('falling');
  }

  function showStory() {
    dragRef.current = null;
    focusStoryRef.current = true;
    setNearBin(false);
    setPhase('intro');
  }

  const isReward = ['reward', 'complete'].includes(phase) || (phase === 'intro' && xp > 0);
  const storyVisible = phase === 'intro';
  const status = isReward ? 'complete' : phase === 'dragging' ? (nearBin ? 'release' : 'dragging') : ['photo', 'upload'].includes(phase) ? phase : 'ready';
  const StatusIcon = isReward ? Check : phase === 'photo' ? Camera : phase === 'upload' ? CloudUpload : MousePointer2;

  return <div ref={sceneRef} className={`landing-cleanup ${loading ? 'is-loading' : ''}`} data-artwork={mode} data-theme={theme} data-phase={phase} data-near-bin={nearBin} aria-busy={loading || undefined}>
    <div className="cleanup-stage" ref={stageRef}>
      <LandingCourtyardArtwork mode={mode} theme={theme} bounds={bounds} />
      {['falling', 'ready', 'dragging', 'returning', 'tossing'].includes(phase) && <>
        <span className="cleanup-paper-shadow" style={{ left: `${HOME.x / 6}%` }} />
        <button ref={paperRef} key={round} type="button" className="cleanup-paper" style={{ left: `${position.x / 6}%`, top: `${position.y / 4.6}%` }}
          disabled={loading || !['ready', 'dragging'].includes(phase)} tabIndex={loading ? -1 : 0}
          aria-label={t('landingCleanup.paper')} aria-describedby={`${id}-instructions`}
          onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={cancelDrag} onLostPointerCapture={cancelDrag}
          onClick={event => { if (event.detail === 0 && phase === 'ready') { focusStoryRef.current = true; toss(); } }}
          onKeyDown={event => { if (phase === 'ready' && ['Enter', ' '].includes(event.key)) { event.preventDefault(); focusStoryRef.current = true; toss(); } }}>
          <img src={paperArt} alt="" draggable="false" />
        </button>
      </>}
      {['reward', 'complete'].includes(phase) && <ExperienceReward className="cleanup-reward" amount={REWARD} locale={i18n.language} unit={t('landingCleanup.unit')} caption={t('landingCleanup.rewardCaption')} onComplete={() => { setXp(REWARD); setPhase('complete'); }} />}
    </div>
    <div className="cleanup-scene-shade" aria-hidden="true" />
    {phase === 'photo' && <><div className="cleanup-camera-frame"><span /><span /><span /><span /><Camera size={28} /><span className="cleanup-camera-caption">{t('landingCleanup.photoCaption')}</span></div><div className="cleanup-flash" /></>}
    {phase === 'upload' && snapshot && <div className="cleanup-upload-card" style={{ '--cleanup-photo-aspect': snapshot.aspect }}>
      <div className="cleanup-photo">
        <LandingCourtyardArtwork mode={snapshot.mode} theme={snapshot.theme} thumbnail bounds={snapshot.bounds} />
        <div className="cleanup-scene-shade" aria-hidden="true" />
        <span><Check size={12} />{t('landingCleanup.photoDone')}</span>
      </div>
      <div className="cleanup-upload-details"><GoodvaleLogo className="cleanup-upload-logo" alt="" /><strong>Goodvale</strong><CloudUpload size={20} aria-hidden="true" /><span>{t('landingCleanup.uploadCaption')}</span></div>
      <div className="cleanup-upload-track"><span /></div>
    </div>}
    <div className="cleanup-story" ref={storyRef} aria-hidden={!storyVisible} inert={!storyVisible}>
      <div className="landing-hero-layout">{children}</div>
    </div>
    <div className="cleanup-instructions" id={`${id}-instructions`} hidden={storyVisible}>
      <div className="cleanup-status" role="status" aria-live="polite" aria-atomic="true"><StatusIcon size={17} aria-hidden="true" /><span>{t(`landingCleanup.status.${status}`)}{isReward && <span className="cleanup-sr-only"> {t('landingCleanup.experience', { count: REWARD })}</span>}</span></div>
      <span className="cleanup-input-hint">{t('landingCleanup.hint')}<span className="cleanup-sr-only"> {t('landingCleanup.keyboard')}</span></span>
    </div>
    <div className="cleanup-controls">
      {!storyVisible && <button type="button" className="cleanup-discover" tabIndex={loading ? -1 : 0} onClick={showStory}>{t('landingCleanup.continue')}<ArrowDown size={15} aria-hidden="true" /></button>}
      <button type="button" className="cleanup-restart" tabIndex={loading ? -1 : 0} onClick={replay}><RotateCcw size={15} aria-hidden="true" />{t('landingCleanup.restart')}</button>
    </div>
  </div>;
}
